/**
 * "Add yours": a video that the artist already has counts as a kit asset (spec
 * `2026-10-09-music24-music-video-editor-design.md` §3, owner decisions D2–D6). The artist links a
 * YouTube video or uploads the file. It counts as made, costs no credits and is not in the kit zip.
 *
 * The rules of each asset type, the YouTube link parser and the sound check result live here. The
 * web dialog, the API routes and the tests read this one file. No imports but a type, so every
 * project may read it.
 */
import type { StudioThemeId } from './studioThemes';

const MB = 1024 * 1024;
const GIB = 1024 * MB;

// ---------------------------------------------------------------------------------------------
// Sources and themes
// ---------------------------------------------------------------------------------------------

/**
 * Where a kit asset version comes from (`KitAssetModel.source`). `made`: Music24 rendered it.
 * `upload`: the artist uploaded the file. `youtube`: the artist linked a YouTube video (no file).
 */
export const KIT_ASSET_SOURCES = ['made', 'upload', 'youtube'] as const;
export type KitAssetSource = (typeof KIT_ASSET_SOURCES)[number];

/** The source of a stored or decoded asset. A missing or unknown value (an old row, a newer server) is `made`. */
export function kitAssetSourceOf(value: unknown): KitAssetSource {
  return typeof value === 'string' && (KIT_ASSET_SOURCES as readonly string[]).includes(value)
    ? (value as KitAssetSource)
    : 'made';
}

/** True for a version that the artist added (an upload or a YouTube link). */
export function isAddedSource(source: KitAssetSource): boolean {
  return source !== 'made';
}

/** The asset types that take "Add yours" (D3). */
export const ADDED_ASSET_THEMES = [
  'musicVideo',
  'lyricVideo',
  'visualizer',
  'canvasLoop',
] as const satisfies readonly StudioThemeId[];
export type AddedAssetTheme = (typeof ADDED_ASSET_THEMES)[number];

export function isAddedAssetTheme(themeId: unknown): themeId is AddedAssetTheme {
  return typeof themeId === 'string' && (ADDED_ASSET_THEMES as readonly string[]).includes(themeId);
}

/** How the artist adds a video: a file upload, or a YouTube link. */
export const ADDED_ASSET_INPUTS = ['file', 'youtube'] as const;
export type AddedAssetInput = (typeof ADDED_ASSET_INPUTS)[number];

// ---------------------------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------------------------

/** A shape rule. `portrait`: the height over the width is `ratio` (± `tolerance`), and the height is at least `minHeightPx`. */
export interface AddedAssetShapeRule {
  kind: 'portrait';
  /** Height / width. 9:16 is 16 / 9. */
  ratio: number;
  tolerance: number;
  minHeightPx: number;
}

export interface AddedAssetRule {
  inputs: readonly AddedAssetInput[];
  /** The MIME types of an upload. */
  contentTypes: readonly string[];
  /** A sanity cap (D5: no limit of ours beyond it). */
  maxBytes: number;
  minSec: number;
  /** Null: no upper limit. */
  maxSec: number | null;
  /** Null: any shape. */
  shape: AddedAssetShapeRule | null;
  /** A warning (never a refusal) when the length differs from the song by more than this. Null: no warning. */
  lengthWarningSec: number | null;
}

const LONG_VIDEO_RULE: AddedAssetRule = {
  inputs: ['file', 'youtube'],
  contentTypes: ['video/mp4', 'video/quicktime'],
  maxBytes: 50 * GIB,
  minSec: 10,
  maxSec: null,
  shape: null,
  lengthWarningSec: 15,
};

/** The rules of each asset type (spec §3.3). */
export const ADDED_ASSET_RULES: Readonly<Record<AddedAssetTheme, AddedAssetRule>> = {
  musicVideo: LONG_VIDEO_RULE,
  lyricVideo: LONG_VIDEO_RULE,
  visualizer: LONG_VIDEO_RULE,
  // Spotify takes the Canvas as an MP4 file only, 3 to 8 s, 9:16, at least 720 px tall, at most 8 MB.
  canvasLoop: {
    inputs: ['file'],
    contentTypes: ['video/mp4'],
    maxBytes: 8 * MB,
    minSec: 3,
    maxSec: 8,
    shape: { kind: 'portrait', ratio: 16 / 9, tolerance: 0.02, minHeightPx: 720 },
    lengthWarningSec: null,
  },
};

/** The file extension of each upload type. The server takes the extension from here, never from the file name. */
export const ADDED_VIDEO_EXTENSIONS: Readonly<Record<string, string>> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
};

/** True when the asset type takes this input. */
export function addedInputAllowed(themeId: string, input: AddedAssetInput): boolean {
  return isAddedAssetTheme(themeId) && ADDED_ASSET_RULES[themeId].inputs.includes(input);
}

export type AddedUploadStartRefusal = 'notAllowed' | 'type' | 'tooLarge';

/**
 * The check of the upload start (before a byte goes up): the asset type takes a file, the type is
 * allowed and the size is under the cap. Null: the upload may start.
 */
export function checkAddedUploadStart(
  themeId: string,
  file: { contentType: string; sizeBytes: number }
): AddedUploadStartRefusal | null {
  if (!isAddedAssetTheme(themeId) || !addedInputAllowed(themeId, 'file')) {
    return 'notAllowed';
  }
  const rule = ADDED_ASSET_RULES[themeId];
  if (!rule.contentTypes.includes(file.contentType.toLowerCase())) {
    return 'type';
  }
  if (!Number.isFinite(file.sizeBytes) || file.sizeBytes <= 0 || file.sizeBytes > rule.maxBytes) {
    return 'tooLarge';
  }
  return null;
}

/** What ffprobe (or the browser) measured of an uploaded video. */
export interface AddedVideoMeasures {
  contentType: string;
  sizeBytes: number;
  /** Null: the length could not be read. */
  durationSec: number | null;
  width: number | null;
  height: number | null;
}

export type AddedVideoRefusal =
  | 'notAllowed'
  | 'type'
  | 'tooLarge'
  | 'unreadable'
  | 'tooShort'
  | 'tooLong'
  | 'shape'
  | 'tooSmall';

export type AddedVideoWarning = { kind: 'lengthDiffers'; videoSec: number; songSec: number };

export type AddedVideoCheck = { ok: true; warnings: AddedVideoWarning[] } | { ok: false; reason: AddedVideoRefusal };

/**
 * The check of an uploaded video against the rules of its asset type (the complete step, 400
 * `M24_ADDED_REFUSED` with `reason`). A length that differs from the song only warns.
 * `songDurationSec` null: no length warning (no master yet).
 */
export function checkAddedVideo(
  themeId: string,
  measures: AddedVideoMeasures,
  songDurationSec: number | null
): AddedVideoCheck {
  const start = checkAddedUploadStart(themeId, measures);
  if (start !== null) {
    return { ok: false, reason: start };
  }
  const rule = ADDED_ASSET_RULES[themeId as AddedAssetTheme];
  const { durationSec, width, height } = measures;
  if (durationSec === null || !Number.isFinite(durationSec) || durationSec <= 0) {
    return { ok: false, reason: 'unreadable' };
  }
  if (durationSec < rule.minSec) {
    return { ok: false, reason: 'tooShort' };
  }
  if (rule.maxSec !== null && durationSec > rule.maxSec) {
    return { ok: false, reason: 'tooLong' };
  }
  if (rule.shape !== null) {
    if (width === null || height === null || width <= 0 || height <= 0) {
      return { ok: false, reason: 'unreadable' };
    }
    if (Math.abs(height / width - rule.shape.ratio) > rule.shape.ratio * rule.shape.tolerance) {
      return { ok: false, reason: 'shape' };
    }
    if (height < rule.shape.minHeightPx) {
      return { ok: false, reason: 'tooSmall' };
    }
  }
  const warnings: AddedVideoWarning[] = [];
  if (
    rule.lengthWarningSec !== null &&
    songDurationSec !== null &&
    Number.isFinite(songDurationSec) &&
    songDurationSec > 0 &&
    Math.abs(durationSec - songDurationSec) > rule.lengthWarningSec
  ) {
    warnings.push({ kind: 'lengthDiffers', videoSec: durationSec, songSec: songDurationSec });
  }
  return { ok: true, warnings };
}

// ---------------------------------------------------------------------------------------------
// YouTube
// ---------------------------------------------------------------------------------------------

/** The YouTube facts of a linked version (`KitAssetModel.youtube`, spec §3.1). */
export interface KitAssetYouTube {
  videoId: string;
  title: string;
  channelTitle: string;
  channelId: string | null;
  thumbnailUrl: string;
  durationSec: number | null;
  /** True when the video is on the connected YouTube channel of the artist. */
  ownChannel: boolean;
  /**
   * The views of the synced post of the connected channel when the version was linked; null when
   * the video is not on that channel or the read has no number. Absent on a version linked before
   * the field. The daily social sync does not update it.
   */
  views?: number | null;
}

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
];
/** The path forms that carry the id as the next path part. */
const YOUTUBE_ID_PATHS = ['shorts', 'embed', 'live', 'v'];

/**
 * The 11-character video id of a YouTube link, or null. It takes youtu.be links, `watch?v=`,
 * `shorts/`, `embed/`, `live/` and `v/` on youtube.com, m.youtube.com, music.youtube.com and the
 * no-cookie host, with or without the scheme, and a bare id.
 */
export function parseYouTubeVideoId(input: string): string | null {
  const text = input.trim();
  if (YOUTUBE_VIDEO_ID.test(text)) {
    return text;
  }
  const match = /^(?:https?:\/\/)?([^/?#\s]+)([^?#\s]*)(\?[^#\s]*)?/i.exec(text);
  if (match === null) {
    return null;
  }
  const host = (match[1] ?? '').toLowerCase().replace(/:\d+$/, '');
  const parts = (match[2] ?? '').split('/').filter((part) => part !== '');
  const query = (match[3] ?? '').slice(1);
  let id: string | undefined;
  if (host === 'youtu.be' || host === 'www.youtu.be') {
    [id] = parts;
  } else if (YOUTUBE_HOSTS.includes(host)) {
    if (parts[0] === 'watch') {
      id = query
        .split('&')
        .map((pair) => pair.split('='))
        .find(([key]) => key === 'v')?.[1];
    } else if (parts[0] !== undefined && YOUTUBE_ID_PATHS.includes(parts[0])) {
      [, id] = parts;
    }
  }
  return id !== undefined && YOUTUBE_VIDEO_ID.test(id) ? id : null;
}

/** The watch page of a video. */
export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/** Lower case, no accents, every run of other characters one space. */
function foldTitle(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * True when the YouTube title holds the song title as whole words, case and accents folded:
 * "LaziiBee - Forests (Official Video)" holds "Forests". An empty song title never matches.
 */
export function youtubeTitleMatchesSong(title: string, songTitle: string): boolean {
  const song = foldTitle(songTitle);
  if (song === '') {
    return false;
  }
  return ` ${foldTitle(title)} `.includes(` ${song} `);
}

/** A YouTube video is the "best match" when its length is the song length ± this. */
export const YOUTUBE_MATCH_LENGTH_SEC = 15;

/**
 * True when a video of the channel looks like this song (the `bestMatch` candidate, spec §3.3): the
 * title holds the song title and the length is the song length ± `YOUTUBE_MATCH_LENGTH_SEC`. With
 * no song length, the title alone decides.
 */
export function isYouTubeSongMatch(
  video: { title: string; durationSec: number | null },
  song: { title: string; durationSec: number | null }
): boolean {
  if (!youtubeTitleMatchesSong(video.title, song.title)) {
    return false;
  }
  if (song.durationSec === null || video.durationSec === null) {
    return song.durationSec === null;
  }
  return Math.abs(video.durationSec - song.durationSec) <= YOUTUBE_MATCH_LENGTH_SEC;
}

// ---------------------------------------------------------------------------------------------
// Sound check (D4)
// ---------------------------------------------------------------------------------------------

export const SOUND_CHECK_VERDICTS = ['match', 'mismatch', 'unknown'] as const;
export type SoundCheckVerdict = (typeof SOUND_CHECK_VERDICTS)[number];

/** Why a check has no verdict: no master to compare with, a file that does not decode, another error. */
export type SoundCheckReason = 'noMaster' | 'decode' | 'error';

/**
 * The check that the sound of an uploaded music video is the song (`params.soundCheck`). A
 * mismatch is a warning, never a block.
 */
export interface SoundCheck {
  verdict: SoundCheckVerdict;
  /** 0..1, the best normalised chroma correlation. Null with no comparison. */
  similarity: number | null;
  /** The offset of the song in the video, in seconds. Null with no comparison. */
  offsetSec: number | null;
  reason?: SoundCheckReason;
  /** ISO time. */
  checkedAt: string;
}
